import React from 'react';
import { Box, Text, VStack, HStack, useTheme } from '@chakra-ui/react';
import {
  Cell,
  Pie,
  // eslint-disable-next-line no-restricted-imports
  PieChart as RechartsPieChart,
  ResponsiveContainer,
} from 'recharts';

export type PacingHealthData = {
  actualSpend: number;
  idealSpend: number;
  pacingHealth: number; // percentage (0-100)
};

export type PacingHealthChartProps = {
  data: PacingHealthData;
  isLoading?: boolean;
  isError?: boolean;
};

export const PacingHealthChart: React.FC<PacingHealthChartProps> = ({
  data,
  isLoading = false,
  isError = false,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box
        height="350px"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Text>Loading...</Text>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        height="350px"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Text>No data available</Text>
      </Box>
    );
  }

  // Create data for the donut chart
  const chartData = [
    {
      name: 'Completed',
      value: data.pacingHealth,
      color: '#22c55e',
    },
    {
      name: 'Remaining',
      value: 100 - data.pacingHealth,
      color: '#e5e7eb',
    },
  ];

  return (
    <Box height="300px" width="100%">
      <VStack height="100%" justifyContent="center" spacing={4}>
        {/* Donut Chart */}
        <Box height="240px" width="240px" position="relative">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={95}
                outerRadius={110}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
                stroke="none">
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </RechartsPieChart>
          </ResponsiveContainer>

          {/* Center percentage text */}
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            textAlign="center">
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
              {Math.round(data.pacingHealth)}%
            </Text>
          </Box>
        </Box>

        {/* Data values */}
        <HStack spacing={8} justify="center" align="center">
          <VStack spacing={1} align="center">
            <Text fontSize="sm" color="darkGray" fontWeight="medium">
              Actual Spend:
            </Text>
            <Text
              fontSize="lg"
              fontWeight="bold"
              color={theme.colors.chart.line.blue}>
              {data.actualSpend}
            </Text>
          </VStack>
          <VStack spacing={1} align="center">
            <Text fontSize="sm" color="darkGray" fontWeight="medium">
              Ideal Spend:
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="icon.green">
              {data.idealSpend}
            </Text>
          </VStack>
        </HStack>
      </VStack>
    </Box>
  );
};
